import GenericTableComponentClass from '@/types/component/GenericTableComponentClass'

import _ from 'lodash'
import { mixins } from 'vue-class-component'
import type { Component } from 'vue'
import { Prop, Watch } from 'vue-property-decorator'
import BaseComponent from '@/components/calculator/base-components/BaseComponent'
import CalculatorInputField from '@/types/contentful-types/CalculatorInputField'
import {
  Calculate, Calculation, CalculationPurpose, CalculationPurposePurposeEnum, CalculationStateEnum,
  FieldValue,
  FileMessage
} from 'openapi/generated-clients/climatecompass'
import { FormValidation } from '@/shared/FormValidation'
import { calculateClient, calculationClient, fileClient } from '@/shared/BackendService'
import * as DKFDS from 'dkfds'
import CalculatorPage from '@/types/contentful-types/CalculatorPage'
import { CombinedField } from '@/types/CombinedField'
import ThirdpartyInfo from '@/types/ThirdpartyInfo'
import { Constants } from '@/shared/Constants'
import GenericTableComponent from '@/components/calculator/CalculatorTableComponent.vue'
import store from "@/store";


interface SubmitEvent extends Event {
  submitter: HTMLElement;
}

export default class BaseCalculator extends mixins(BaseComponent) {
  @Prop()
  title?: string
  @Prop()
  tooltip?: string
  @Prop()
  expanded?: boolean
  @Prop()
  subgroupingId?: number
  @Prop()
  enableScopeInputs?: boolean
  @Prop()
  calculatorPageSysId?: string
  @Prop({ default: false })
  showEFSelection?: boolean
  @Prop({ default: false })
  showRFISelection?: boolean
  @Prop({ default: false })
  isEsgOwnEmissionFactors?: boolean

  @Prop({ default: false })
  basisModule?: boolean

  showEsgFields = true

  currentCalculatorPage = {} as CalculatorPage
  contentfulId = ''
  calculationChanged = false
  files: File[] = []
  fileList: Partial<FileMessage[]> = []
  fields: CombinedField[] = []
  renderedFields: CombinedField[] = []
  scopeFields: CombinedField[] = []
  esgManualFields: CombinedField[] = []

  remarksField = {} as CombinedField
  scope3category = {} as CombinedField

  totalEmission = ''
  calculationList: Calculate[] = []
  currentCalculationLineId? = this.basisModule ? -1 : 0

  genericTableComponentRefs: GenericTableComponent[] = []
  genericTables: GenericTableComponentClass[] = []
  formValidation = new FormValidation()

  showCloseButton = true

  countryEmissionWarningText = ''
  showEmissionWarning = false
  thirdPartyInfo = {} as ThirdpartyInfo

  working = false

  hasBeenRecalculated = false
  lastSavedCalculationId = 0

  isReload = false
  emcId = -1
  emfId = -1
  emfKey = ''

  get isReadOnly() {
    return this.calculation.state === 'readonly' || this.calculation.state === 'finished_duplicated'
  }

  $refs!: {
    field0: HTMLElement
    formErrorsAlert: HTMLElement
    saveButton: HTMLElement
  }

  @Watch('subgroupingId')
  watchSubgroupingId() {
    this.isReload = true
    this.init()
  }

  async beforeMount() {
    await this.init()
  }

  async init() {
    const countryName = this.basisModule ? this.getContentfulString('country.name.DK') : await this.fetchCountryName()
    await this.loadFields().then(async () => {
      this.countryEmissionWarningText = this.countryEmissionWarning.replace('%', countryName ?? '')
    })
      .catch((error) => {
        console.warn('[Advarsel] Indlæsningsfejl: ' + error)
      })
    if (this.calculatorPageSysId) {
      const full = await this.getPageFull(this.calculatorPageSysId)
      this.contentfulId = full.sys.id
      this.currentCalculatorPage = full.fields
      //this.currentCalculatorPage = await this.getPage(this.calculatorPageSysId)
    }

    if (this.showEFSelection) {
      this.eventHub.on('ef-updated', async (arg: string) => {
        await this.updatedEmissionFactorToggle(arg)
      })
    }

    if (this.showRFISelection) {
      this.eventHub.on('rfi-updated', async (arg: string) => {
        await this.updatedRfiToggle(arg)
      })
    }
  }

  mounted() {
    DKFDS.init()
    this.eventHub.emit('calculator-mounted')
  }

  updated() {
    DKFDS.init()
    this.eventHub.emit('calculator-mounted')
  }

  setGenericTableComponentRef(comp: any) {
    if (comp) {
      this.genericTableComponentRefs[comp.content.calculateId] = comp
    }
  }

  calculateTotalEmission(calculation: Calculate) {
    if (!calculation.fieldValues) {
      return '0'
    }

    const scopeFields =
      this.basisModule ?
        this.scopeFields
          .filter(x => x.calculatorSlotPosition === Constants.CALCULATOR_SLOT_SCOPE1
            || x.calculatorSlotPosition === Constants.CALCULATOR_SLOT_SCOPE2)
          .map((x: FieldValue) => {
            return x.id
          })
        :
        this.scopeFields
          .filter(x => x.calculatorSlotPosition === Constants.CALCULATOR_SLOT_SCOPE1
            || x.calculatorSlotPosition === Constants.CALCULATOR_SLOT_SCOPE2
            || x.calculatorSlotPosition === Constants.CALCULATOR_SLOT_SCOPE3)
          .map((x: FieldValue) => {
            return x.id
          })

    return calculation.fieldValues
      .filter(x => scopeFields.includes(x.id))
      .reduce((sum, cur) => sum + (!isNaN(Number(cur.value)) ? Number(cur.value) : 0), 0)
  }

  setTotalEmission(val?: string | number) {
    if (val) {
      const prettified = this.doPrettify(val, 2)
      this.totalEmission = prettified ? prettified.toString() : ''
    } else {
      this.totalEmission = val === 0 ? '0,00' : ''
    }
  }

  showToastAndDelayHide(title: string, type: number, cypressIdentifier: number | undefined = undefined) {
    switch (type) {
      case this.constants.TOAST_TYPE_CALCULATOR_DELETE:
        this.addToast('TOAST_TYPE_CALCULATOR_DELETE', 'success',
          this.deleteRowToastHeader,
          this.deleteRowToastMessage.replace('#rowname#', title),
          this.showCloseButton)
        break
      case this.constants.TOAST_TYPE_CALCULATOR_SAVED:
        this.addToast('TOAST_TYPE_CALCULATOR_SAVED', 'success',
          this.saveRowToastHeader,
          this.saveRowToastMessage.replace('#rowname#', title),
          this.showCloseButton)
        break
      case this.constants.TOAST_TYPE_CALCULATOR_UPDATED:
        this.addToast('TOAST_TYPE_CALCULATOR_UPDATED', 'success',
          this.updateRowToastHeader,
          this.updateRowToastMessage.replace('#rowname#', title),
          this.showCloseButton)
        break
      case this.constants.TOAST_TYPE_CALCULATOR_ERROR:
        this.addToast('TOAST_TYPE_CALCULATOR_ERROR', 'error',
          this.saveErrorToastHeader,
          this.saveErrorToastMessage.replace('#rowname#', title),
          this.showCloseButton)
        break
    }
  }

  mapCalculationFieldValues(calculation: Calculate, field: CombinedField) {
    const value = calculation.fieldValues.find((res) => field.id === res.id)?.value || ''
    //console.log("mapCalculationFieldValues", field.valueType, field, value)

    //Special conditional for biofuel percentage - if field is empty it should be presented as empty rather than 0, as empty means use default value
    if (value === '' && field.id === this.constants.INPUT_BIOBRÆNDSTOFANDEL) {
      return value
    }

    if (field.valueType !== 'STRING') {
      return field.valueType === 'DECIMAL' && field.step === 1 ? String(Math.round(Number(value))) : String(this.formatNumber(value))
    }
    return value
  }

  getRowColHeaders(calculate: Calculate) {
    const rowColumnsHeaders = [
      this.getRowColHeadersLine1(calculate),
      [
        this.scopeFields[0]?.lable,
        this.scopeFields[1]?.lable,
        this.scopeFields[2]?.lable,
        this.totalEmissionsTons,
        this.scopeFields[3]?.lable
      ]
    ]
    if (this.subgroupingId && Constants.GROUPS_WITH_POSSIBLE_OWN_ESG_FACTORS.includes(this.subgroupingId)) {
      rowColumnsHeaders.push([this.esgManualFields[0].lable, this.esgManualFields[1].lable, this.esgManualFields[2].lable, this.esgManualFields[3].lable, this.esgManualFields[4].lable])
    }
    return rowColumnsHeaders
  }

  getRowColHeadersLine1(calculate: Calculate) {
        if (this.subgroupingId && Constants.GROUPS_WITH_POSSIBLE_OWN_ESG_FACTORS.includes(this.subgroupingId)) {
            return [this.renderedFields[0]?.lable, this.renderedFields[1]?.lable, this.renderedFields[2]?.lable,
                ((this.renderedFields[3].name === "ESG"
                  || this.renderedFields[3].id === 1141
                  || (this.renderedFields[3].id === 47 && this.subgroupingId === Constants.GROUP_TRANSPORT_ANDET))
                    && calculate.fieldValues?.find(
                        (field: { id: number }) => field.id === this.renderedFields[3]?.id)?.value ? this.renderedFields[3].lable : ""),
                this.renderedFields[4]?.lable]
        }
        if (this.subgroupingId && this.subgroupingId === Constants.GROUP_FORBRUG_AF_ELEKTRICITET) {
          // note field 4 is moved - we do not want the change org order as this requires recalculate of old calculations
          return [this.renderedFields[0]?.lable, this.renderedFields[1]?.lable, this.renderedFields[4]?.lable, this.renderedFields[2]?.lable, this.renderedFields[3]?.lable]
        } else if (this.subgroupingId && this.subgroupingId === Constants.GROUP_EGNE_OG_LEASEDE_KØRETØJER) {
          if (this.isCarOrVansElectricOnSg(calculate.fieldValues, Constants.GROUP_EGNE_OG_LEASEDE_KØRETØJER, this.subgroupingId)) {
            return [this.renderedFields[0]?.lable, this.renderedFields[1]?.lable, this.renderedFields[2]?.lable, this.renderedFields[3]?.lable, this.renderedFields[4]?.lable]
          } else {
            return [this.renderedFields[0]?.lable, this.renderedFields[1]?.lable, this.renderedFields[2]?.lable, this.renderedFields[3]?.lable, this.renderedFields[5]?.lable]
          }
        } else {
          return [this.renderedFields[0]?.lable, this.renderedFields[1]?.lable, this.renderedFields[2]?.lable, this.renderedFields[3]?.lable, this.renderedFields[4]?.lable]
        }
  }

  isCarOrVansElectricOnSg(fieldValues : Array<FieldValue>, sg: number, subgroupingId?: number): boolean {
    if (subgroupingId !== sg) {
      return false;
    }
    for (const value of fieldValues) {
      if (value.id === Constants.INPUT_DROPDOWN_TRANSPORT_DRIVMIDDEL_E) {
        if (
          value.fieldEnumId === Constants.FIELD_ENUM_ID_VANS_ELECTRIC ||
          value.fieldEnumId === Constants.FIELD_ENUM_ID_CAR_ELECTRIC
        ) {
          return true;
        }
      }
    }
    return false;
  }

  hasBioFieldActiveOnSg(fieldValues : Array<FieldValue>, sg: number, subgroupingId?: number): boolean {
    if (subgroupingId !== sg) {
      return false;
    }
    const nonElectric = !this.isCarOrVansElectricOnSg(fieldValues, sg, subgroupingId)
    for (const value of fieldValues) {
      if (value.id === Constants.INPUT_ENHED_KM_L_KWH) {
        if (value.fieldEnumId === Constants.FIELD_ENUM_ID_L_FIELD_51 && nonElectric) {
          return true;
        }
      }
    }
    return false;
  }

  getRowColData(calculate: Calculate) {
    let rowColData0
    if (this.subgroupingId && this.subgroupingId === Constants.GROUP_FORBRUG_AF_ELEKTRICITET) {
      rowColData0 = [
        calculate.fieldValues?.find((field: { id: number }) => field.id === this.renderedFields[0]?.id)?.value,
        this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[1]), ''),
        this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[4]), ''),
        this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[2]), ''),
        this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[3]), '')
      ]
    } else if (this.subgroupingId && this.subgroupingId === Constants.GROUP_EGNE_OG_LEASEDE_KØRETØJER) {
      if (this.isCarOrVansElectricOnSg(calculate.fieldValues, Constants.GROUP_EGNE_OG_LEASEDE_KØRETØJER, this.subgroupingId)) {
        rowColData0 = [
          calculate.fieldValues?.find((field: { id: number }) => field.id === this.renderedFields[0]?.id)?.value,
          this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[1]), ''),
          this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[2]), ''),
          this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[3]), ''),
          this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[4]), '')
        ]
      } else {
        rowColData0 = [
          calculate.fieldValues?.find((field: { id: number }) => field.id === this.renderedFields[0]?.id)?.value,
          this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[1]), ''),
          this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[2]), ''),
          this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[3]), ''),
          this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[5]), '')
        ]
      }
    } else {
      rowColData0 = [
        calculate.fieldValues?.find((field: { id: number }) => field.id === this.renderedFields[0]?.id)?.value,
            this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[1]), ''),
            this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[2]), ''),
            this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[3]), ''),
            this.parseFieldNameFromDb(this.getFieldValue(calculate, this.renderedFields[4]), '')
        ]
    }
    const rowColData1 = [
      calculate.fieldValues?.find((field: { id: number }) => field.id === this.scopeFields[0]?.id)?.value,
      calculate.fieldValues?.find((field: { id: number }) => field.id === this.scopeFields[1]?.id)?.value,
      calculate.fieldValues?.find((field: { id: number }) => field.id === this.scopeFields[2]?.id)?.value,
      calculate.fieldValues ? this.calculateTotalEmission(calculate) : "",
      calculate.fieldValues?.find((field: { id: number }) => field.id === this.scopeFields[3]?.id)?.value
    ]

    const rowColumnData = [rowColData0, rowColData1]
    if (Constants.GROUPS_WITH_POSSIBLE_OWN_ESG_FACTORS.includes(calculate.subgroupingId)) {
          rowColumnData.push([
            calculate.fieldValues?.find((field: { id: number }) => field.id === this.esgManualFields[0]?.id)?.value,
            calculate.fieldValues?.find((field: { id: number }) => field.id === this.esgManualFields[1]?.id)?.value,
            calculate.fieldValues?.find((field: { id: number }) => field.id === this.esgManualFields[2]?.id)?.value,
            calculate.fieldValues?.find((field: { id: number }) => field.id === this.esgManualFields[3]?.id)?.value,
            calculate.fieldValues?.find((field: { id: number }) => field.id === this.esgManualFields[4]?.id)?.value
          ])
    }
    return rowColumnData
  }

  getFieldValue(calculate: Calculate, renderedField: CombinedField){
        let field = calculate.fieldValues?.find((field: {
            id: number
        }) => field.id === renderedField?.id);

        if(!field){
            // If it is a "own emissionsfactor" unit field, and we did not get a direct hit above, attempt to find "old" value from previous dropdown list (id 47)
            if(Constants.GROUPS_OWN_EMISSION_FACTORS.find(x => x === calculate.subgroupingId)
                && Constants.UNIT_FIELDS_OWN_EMISSION_FACTORS.find(x => x === renderedField?.id)){
                field = calculate.fieldValues?.find((field: {
                    id: number
                }) => field.id === 47);
            }
        }
        let value = field?.value;
        if (field?.id === Constants.FIELD_FORBRUG_EL_EGEN_PROD
          && calculate.subgroupingId === Constants.GROUP_FORBRUG_AF_ELEKTRICITET
          && this.basisModule
          && value === '') {
          value = '0'
        }
        if(value){
            return value;
        }
        return '';
  }


  get scope3categoryLabel() {
    return this.store.getters.getContentfulContent.findSimpleText('masterdata.scope3categoryLabel')
  }

  setUseInEsg(val: boolean) {
    this.showEsgFields = val
    this.eventHub.emit('use-in-esg-updated', this.showEsgFields)
  }

  get useInEsg(): boolean {
    return this.showEsgFields
  }

  calculateToTable(calculate: Calculate, isEdit = false) {
    const s3Selection = this.getS3Selection(calculate)
    const table: GenericTableComponentClass = {
      remarksFieldLabel: this.remarksField.lable,
      remarksFieldValue: calculate.fieldValues?.find((field) => field.id === this.constants.INPUT_BEMÆRKNING)?.value,
      calculationId: calculate.calculationId,
      calculateId: calculate.id,
      readonly: this.isReadOnly,
      fieldValues: calculate.fieldValues,
      isEdit: isEdit,
      createdBy: calculate.createdBy,
      createdTime: calculate.createdTime,
      updatedBy: calculate.updatedBy,
      updatedTime: calculate.updatedTime,
      state: calculate.state,
      scope3CategoryLabel: this.scope3categoryLabel,
      scope3CategorySelection: s3Selection, //this.asContentfulString(s3Selection),
      emcId: calculate.emissionCoefficentId,
      emf: calculate.emissionCoefficentMasterKey,
      information: calculate.information,
      files: calculate.files ? calculate.files : undefined,
      showWarning: calculate.showWarning
    }
    if (this.getRowColData(calculate)) {
      table.rowColData = this.getRowColData(calculate)
    }
    if (this.getRowColHeaders(calculate)) {
      table.rowColHeaders = this.getRowColHeaders(calculate)
    }
    return table
  }

  setGenericTables(targetCalculate?: Calculate, isEdit = false) {
    if (targetCalculate) {
      // Ensure that no other is marked as edit
      this.genericTables.forEach((table: GenericTableComponentClass) => table.isEdit = false)

      const first = targetCalculate.id
      this.genericTables = this.genericTables.sort((a, b) => {
        return a.calculateId === first ? -1 : b.calculateId === first ? 1 : 0
      })
      const editedTable = this.genericTables.find((table) => table.calculateId === first)
      if (editedTable) editedTable.isEdit = isEdit
    } else {
      this.genericTables = []

      const listToShow = [...this.calculationList].sort((a, b) => String(this.getRowColData(a)[0]).localeCompare(String(this.getRowColData(b)[0])))
      for (const calculate of listToShow) {
        this.genericTables.push(this.calculateToTable(calculate))
      }
    }
  }

  updateGenericTable(changedCalculate: Calculate, isDeletion = false) {
    const updatedEntry = this.genericTables.find((table) => table.calculateId == changedCalculate.id)
    if (updatedEntry && !isDeletion) {
      Object.assign(updatedEntry, this.calculateToTable(changedCalculate))
      this.genericTables = this.genericTables.sort((a, b) => String(a.rowColData ? a.rowColData[0] : '').localeCompare(String(b.rowColData ? b.rowColData[0] : '')))
    } else {
      // Is new
      if (isDeletion) {
        const indexToRemove = this.genericTables.findIndex((table) => table.calculateId === changedCalculate.id)
        this.genericTables.splice(indexToRemove, 1)
      } else {
        this.genericTables.push(this.calculateToTable(changedCalculate))
      }
    }
  }

  updateCalculationList(data: any[], fullLoad = false) {
    if (data.length > 0 || this.isReload) {
      this.isReload = false
      const isInitialLoad = this.calculationList.length === 0
      this.calculationList = data.filter((x) => this.calculationList.some((y) => x.id === y.id))
      data.forEach((cal: Calculate) => {
        const existing = this.calculationList.find((x: Calculate) => {
          return cal.id === x.id
        })
        cal.fieldValues.forEach((field) => {
          //Do not remove this mapping, it is needed as the selected fieldEnumId is transported from backend in a seperate field
          if (field && field.value && typeof field.value === "string" && field.fieldEnumId) {
            field.value = Constants.FIELD_ENUM_ID_PREFIX + field.fieldEnumId
            //console.log("UCL - Set field value from fieldenumid + ["+field.value+"]", this.scope3category.id, (field.id == this.scope3category.id), field)
          }
        })
        if (existing && !_.isEqual(existing, cal)) {
          Object.assign(existing, cal)
          this.updateCalculateEntry(cal)
        } else if (!existing) {
          this.calculationList.push(cal)
          if (!isInitialLoad) {
            this.updateCalculateEntry(cal)
          }
        }
      })
      // Reload all if isInitialLoad - first load
      if (isInitialLoad || fullLoad) this.setGenericTables()
    }
  }

  updateCalculationListBasisModule(data: Calculate) {
    if (!data) return
    this.isReload = false
    const isInitialLoad = this.calculationList.length === 0
    // Ensure data.id exists
    if (!data.id) {
      console.warn('updateCalculationList: Missing ID, cannot process.', data)
      return
    }
    // Find existing calculation
    const existing = this.calculationList.find(cal => cal.id === data.id)
    if (existing) {
      Object.assign(existing, data)
      this.updateCalculateEntry(data)
    } else {
      this.calculationList.push(data)
      this.updateCalculateEntry(data)
    }
    // Reload tables if first load
    if (isInitialLoad) {
      this.setGenericTables()
    }
  }


  updateCalculateEntry(calculate: Calculate) {
    const updatedCalculate = this.calculationList.find((calc) => calc.id === calculate.id)
    if (updatedCalculate) {
      // If edited
      Object.assign(updatedCalculate, calculate)
      this.updateGenericTable(updatedCalculate)
    } else {
      //  If new
      this.updateGenericTable(calculate)
    }
  }

  thirdpartyVatNo() {
    const thirdPartyInfoRaw = sessionStorage.getItem('thirdPartyInfo') // context.state.thirdpartyInfo.behalfOfVatNr
    if (thirdPartyInfoRaw) {
      this.thirdPartyInfo = JSON.parse(thirdPartyInfoRaw)
    }
    return this.thirdPartyInfo.behalfOfVatNr ? this.thirdPartyInfo.behalfOfVatNr : 'null'
  }

  async loadCalculations(targetCalculateId = 0) {
    if (!this.calculation.id) return
    await calculateClient
      .getCalculationLines(this.calculation.id, this.subgroupingId ?? 0, this.thirdpartyVatNo())
      .then((res) => {
        if (targetCalculateId > 0) {
          const updatedCalculate = res.data.find((calc) => calc.id === targetCalculateId)
          if (updatedCalculate) {
            updatedCalculate.fieldValues.forEach((field) => {
              if (field && field.value && typeof field.value === 'string' && field.fieldEnumId) {
                field.value = Constants.FIELD_ENUM_ID_PREFIX + field.fieldEnumId
                //console.log("UCE - Set field value from fieldenumid + ["+field.value+"]", this.scope3category.id, (field.id == this.scope3category.id), field)
              }
            })
            this.updateCalculateEntry(updatedCalculate)
          }
        } else {
          this.updateCalculationList(res.data[0] ? res.data.reverse() : [], true)
          DKFDS.init()
        }
      })
  }

  async loadFields(targetCalculateId = 0) {
    /*
    const before = await calculateClient.getCalculationFields(Number(this.subgroupingId!))
            for (const item of before.data) {
        if (item.name === 'Scope3_Kategori') {
                        }
    }*/
    await calculateClient
      .getCalculationFields(Number(this.subgroupingId!))
      .then(async (result) => {
        this.fields = result.data.map((field) => {
          if (field.valueType === 'DROPDOWN' || field.valueType === 'UNIT') {
            if (field.possibleValues?.length === 1) {
              //console.log("loadFields length 1 field_id" + field.id, field.possibleValues[0])
              return Object.assign(field, { value: Constants.FIELD_ENUM_ID_PREFIX + field.possibleValues[0].id }, { fieldEnumId: field.possibleValues[0].id })
            } else {
              // take default value if exist
              if (field.possibleValues) {
                for (const item of field.possibleValues) {
                  if (item.isDefault) {
                    return Object.assign(field, { value: Constants.FIELD_ENUM_ID_PREFIX + item.id }, { fieldEnumId: field.possibleValues[0].id })
                  }
                }
              }
              // default
              return Object.assign(field, { value: '' }, { fieldEnumId: -1 })
            }
          }
          // default
          return Object.assign(field, { value: '' }, { fieldEnumId: undefined })
        })
        for (const field of this.fields) {
          if (field.contentfulId) {
            try {
              const updatedField = await this.getPage<CalculatorInputField>(field.contentfulId)
              Object.assign(field, updatedField) // Update the field without reassigning it
              field.lable = field.lable?.replaceAll('\\n', '<br />') // Update field.lable
            } catch (error: any) {
              console.error(error) // Handle errors appropriately
            }
          }
        }

        this.assignFields()

        this.setTotalEmission('')
        if(!this.basisModule){
          await this.loadCalculations(targetCalculateId)
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  /***
   * assignFields replaces old index based mapping of fields
   */
  assignFields() {
    this.remarksField = this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_BEMAERKNINGER)
    this.scope3category = this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_SCOPE3KATEGORI)
    //console.log("scope3category", this.scope3category)
    if (this.scope3category && this.scope3category.possibleValues) {
      this.scope3category.tooltip = this.asContentfulTooltip(Number(this.subgroupingId!))
    }
    // https://jira.erst.dk/browse/KK2-1627 Numerisk sortering af "streng" fra Contentful
    this.scope3category.possibleValues = this.scope3category.possibleValues?.sort(
      (a, b) => this.getLeadingNumberFromString(a.name) - this.getLeadingNumberFromString(b.name))
    this.scopeFields = [
      this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_SCOPE1),
      this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_SCOPE2),
      this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_SCOPE3),
      // SAMLET er ikke mappet i DB
      this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_UDENFOR)
    ]
    if (this.subgroupingId === Constants.GROUP_EGNE_OG_LEASEDE_KØRETØJER) {
      this.renderedFields = [
        this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_BESKRIVELSE),
        this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_FELT2),
        this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_FELT3),
        this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_FELT4),
        this.getFieldById(Constants.FIELD_GUARANTEES_OF_ORIGIN),
        this.getFieldById(Constants.INPUT_BIOBRÆNDSTOFANDEL)
      ]
    } else {
      this.renderedFields = [
        this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_BESKRIVELSE),
        this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_FELT2),
        this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_FELT3),
        this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_FELT4),
        this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_FELT5)
      ]
    }
    if (this.subgroupingId && Constants.GROUPS_WITH_POSSIBLE_OWN_ESG_FACTORS.includes(this.subgroupingId!)) {
      const includeInEsgField = this.getFieldByName(Constants.INPUT_INCLUDE_IN_ESG)
      if (includeInEsgField && !includeInEsgField.value) {
        includeInEsgField.value = 'true'
      }
      this.esgManualFields = [
        includeInEsgField,
        this.getFieldByName(Constants.INPUT_FOSSIL_ENERGY_SHARE_PCT),
        this.getFieldByName(Constants.INPUT_NUCLEAR_ENERGY_SHARE_PCT),
        this.getFieldByName(Constants.INPUT_RENEWABLE_ENERGY_SHARE_PCT),
        this.getFieldByName(Constants.INPUT_ESG_CONVERSION)
      ]
    }
    //console.log("assignField, renderendfields:", this.renderedFields)
  }

  getLeadingNumberFromString(input: string) {
    try {
      return Number(input.split('.')[0])
    } catch (e) {
      console.warn('[CONTENTFUL] Forkert format af tekststrengen: ' + input)
    }
    return 0
  }

  async editCalculation(calculateId: number) {
    this.currentCalculationLineId = calculateId
    const _calculation = this.calculationList.find((calculation) => calculation.id === calculateId)

    /*---Clear CSS-class tokens on calculation edit click---*/
    this.formValidation.formErrors.clear()
    document.querySelectorAll('.form-group').forEach(function(element) {
      element.classList.remove('form-error')
    })
    document.querySelectorAll('.form-input').forEach(function(element) {
      element.classList.remove('changed')
    })

    if (_calculation) {
      this.scope3category.value = this.getS3Selection(_calculation)
      const fv = _calculation.fieldValues.find((res) => this.remarksField.id === res.id)
      if (fv) {
        this.remarksField.value = fv!.value
      } else {
        console.error('Ingen fieldValues, noget er galt i backend med data')
      }

      this.scopeFields.forEach((field) => (field.value = this.mapCalculationFieldValues(_calculation, field)))

      this.renderedFields.forEach((field) => {
        field.value = this.mapCalculationFieldValues(_calculation, field)
        // console.log('editCalculation set dependent/values after map, fieldId='+field.id + ' fieldName=' + field.name
        //   + ' fieldValueType=' + field.valueType + ' fieldValue=' + field.value  + ' fieldEnumId=' + field.fieldEnumId)

        //Set dependent
        if (field.possibleValues?.find((e) => (Constants.FIELD_ENUM_ID_PREFIX + e.id) === field.value)) {
          field.selectedDependents = field.possibleValues?.find((e) => (Constants.FIELD_ENUM_ID_PREFIX + e.id) === field.value)?.dependent
        }

        //Map field enum id to value if present
        if (field.valueType === 'DROPDOWN' || field.valueType === 'DROPDOWN_DEPENDENT' || field.valueType === 'UNIT') {
          //console.log('editCalculation Move value', field.value, field.fieldEnumId)
          if (field.value.startsWith(Constants.FIELD_ENUM_ID_PREFIX) && !field.fieldEnumId || field?.fieldEnumId === -1) {
            field.fieldEnumId = Number(field.value.substring(Constants.FIELD_ENUM_ID_PREFIX.length))
            // console.log('editCalculation Moved value', field.value, field.fieldEnumId)
          } else if (!field.value.startsWith(Constants.FIELD_ENUM_ID_PREFIX) && field.fieldEnumId && field?.fieldEnumId !== -1) {
            field.value = Constants.FIELD_ENUM_ID_PREFIX + field.fieldEnumId
            // console.log('editCalculation Moved value', field.fieldEnumId, field.value)
          }
        }

         //console.log('editCalculation set dependent/values end, fieldId='+field.id + ' fieldName=' + field.name
         //  + ' fieldValueType=' + field.valueType + ' fieldValue=' + field.value  + ' fieldEnumId=' + field.fieldEnumId)
      })

      this.setTotalEmission(this.calculateTotalEmission(_calculation))

      if(this.basisModule){
        this.emcId = _calculation.emissionCoefficentId ?? -1
        this.emfId = _calculation.emissionCoefficentMasterId ?? -1
        this.emfKey = _calculation.emissionCoefficentMasterKey ?? ''
      }

      if (this.subgroupingId && Constants.GROUPS_WITH_POSSIBLE_OWN_ESG_FACTORS.includes(this.subgroupingId!)) {
        //Set manual ESG numbers
        this.esgManualFields.forEach((field) => {
          const value = _calculation.fieldValues.find((res) => field.id === res.id)?.value || ''
          //console.log("esg map", field.name, value, this.mapCalculationFieldValues(_calculation, field))
          if (field.valueType === 'CHECKBOX') {
            field.value = this.mapCalculationFieldValues(_calculation, field)
            //console.log("esg map 2", field.value)
            this.setUseInEsg(field.value === 'true')
          } else {
            field.value = (field.valueType === 'DECIMAL' && field.step === 1 ? String(Math.round(Number(value))) : String(this.formatNumber(value, 5)))
          }
        })
        //console.log("editCalculation before hookEsgDependents", this.esgManualFields)
        this.hookEsgDependents(this.fields)
        //console.log("editCalculation after hookEsgDependents", this.esgManualFields)
      }
      if (calculateId && calculateId > 0 && !this.basisModule) {
        this.fileList = (await fileClient.getFilesByCalculationLineId(calculateId!, this.thirdpartyVatNo())).data.files!
      }
    }

    this.setGenericTables(_calculation, true)

    if (this.$refs.field0) {
      const calculator = document.getElementById('calculatorInput' + this.subgroupingId)
      const field0 = document.getElementById(this.$refs.field0.id)
      if (calculator) {
        field0?.focus()
        this.scrollToTargetWithOffset('calculatorInput' + this.subgroupingId)
      }
    }
  }

  getS3Selection(calculation: Calculate) {
    const scope3Selection = calculation.fieldValues.find(x => x.id === this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_SCOPE3KATEGORI).id)?.fieldEnumId
    if (scope3Selection) {
      return Constants.FIELD_ENUM_ID_PREFIX + scope3Selection
    } else {
      return ''
    }
  }

  async deleteCalculation(calculateLineId?: number) {
    const calculateToDelete = this.calculationList.find((x) => x.id === calculateLineId)
    const calculateToDeleteIndex = this.calculationList.findIndex((x) => x.id === calculateLineId)

    const callback = () => {
      if (calculateToDelete) {
        this.updateGenericTable(calculateToDelete, true)
        this.calculationList.splice(calculateToDeleteIndex, 1)
      }
    }

    if(!this.basisModule){
      await calculateClient.deleteCalculationLine(calculateLineId!, this.thirdpartyVatNo())
        .then(callback)
    } else {
      this.$emit('deleteCalculationLine', calculateLineId)
      callback();
    }
  }

  /*uploadFiles() {
      if (this.files.length === 0) {
          return []
      }
      return Promise.all(
          this.files.map(async (file) => {
              return await fileClient
                  .storeFile(undefined, file, this.thirdpartyVatNo())
                  .then((fileResult) => fileResult.data)
                  .catch(() => {
                      this.formValidation.formErrors.set("file-error", this.fileErrorGeneral)
                      return undefined
                  })
          })
      )
  }*/

  addFile(e: Event) {
    const element = e.target as HTMLInputElement
    //Reinstantiate arrays if they become undefined after deleting last element
    if (!this.files) {
      this.files = []
    }
    if (!this.fileList) {
      this.fileList = []
    }

    if (this.files.length >= 3) {
      this.formValidation.formErrors.set('file-error', this.fileErrorMaxFiles)
      return
    }

    if (!element.files) return

    this.files.push(element.files[0])
    this.fileList.push({
      fileName: element.files[0].name,
      fileType: element.files[0].type
    })
    element.form?.reset()
  }

  async deleteFile(file: FileMessage) {
    if (file.fileId) {
      await fileClient.deleteFile(file.fileId, this.thirdpartyVatNo())
    }

    this.formValidation.formErrors.delete('file-error')
    this.files.splice(
      this.files.findIndex((e) => e.name === file.fileName),
      1
    )
    this.fileList.splice(
      this.fileList.findIndex((e) => e?.fileName === file.fileName),
      1
    )
  }

  truncate(text: string, maxlength: number) {
    if (text.length <= maxlength) {
      return text
    }
    if (text.length >= maxlength) {
      const fileEnding = text.slice(text.lastIndexOf('.'), text.length)
      return text.substring(0, maxlength - fileEnding.length - 3) + '...' + fileEnding
    }
  }

  async updateCalculation(save = false) {
    //console.error("updateCalculation start, save=", save)
    const fieldValuesArray = this.fields.sort((a, b) => {
      return (a.fieldsOrder ?? 0) - (b.fieldsOrder ?? 0)
    }).map((field) => {
      let enumId = field.fieldEnumId
      if (field && field.value && typeof field.value === 'string' && field.value.includes(Constants.FIELD_ENUM_ID_PREFIX)) {
        enumId = parseInt(field.value.substring(Constants.FIELD_ENUM_ID_PREFIX.length))
      }
      if (this.basisModule) {
        // by some strange reason field.value can number but FieldValue.value is a string, fix this here
        // this had been seen in basisModule
        if (typeof field.value === 'number') {
          field.value = String(field.value)
        }
      }
      return { id: field.id, value: field.value, fieldEnumId: enumId }
    })
    // Don't proceed if some or one of the required fields are empty unless scope fields are enabled
    // Proceed even if Description field is empty
    //let skip = this.renderedFields.filter((field) => field.required).some((field) => field.value === "" && field.id !== 1)
    const requiredFields = this.renderedFields.filter(field => field.required)
    //console.log("requiredFields", requiredFields); // Log all required fields
    let skip = requiredFields.some(field => {
      const myInput = window.document.getElementById('calculator-' + this.subgroupingId + '-field-' + field.id) as HTMLInputElement
      //console.log("basecalculator, updatecalculation, field.id="+field.id + " field.value=" + field.value + " myInput:", myInput)
      if (field.id !== 1) {
        if (myInput && myInput.disabled) {
          // if required field but disables then candidate for skip
          //console.log("is disabled", field)
          //console.log("basecalculator, updatecalculation skip return a -> false")
          return false
        } else {
          //console.log("basecalculator, updatecalculation skip return b -> ", (field.value === ""))
          return (field.value === '')
        }
      }
    })
    this.hookEsgDependents(requiredFields)

    //console.log("skip", skip); // Check if the condition is met
    //console.log("updateCalculation, save,skip,fieldValuesArray", save,skip, fieldValuesArray)
    if (this.enableScopeInputs) {
      skip = false
    }
    //console.log("basecalculator, updatecalculation, skip="+skip)
    if (skip) {
      return
    }

    let result: Calculate = {} as Calculate

    if (save) {
      //Remove possible old ESG value
      if (this.isSubgroupWithPossibleOwnEsgFactors()) {
        const esgOldInputFieldId = this.getFieldByName(Constants.INPUT_ESG_OLD)?.id
        if (esgOldInputFieldId) {
          if (fieldValuesArray?.find(f => f?.id === esgOldInputFieldId)?.value) {
            //console.log("fieldValuesArray ESG", fieldValuesArray?.find(f => f?.id === esgOldInputFieldId), fieldValuesArray?.find(f => f?.id === esgOldInputFieldId)?.value)
            fieldValuesArray.find(f => f.id === esgOldInputFieldId)!.value = ''
            fieldValuesArray.find(f => f.id === esgOldInputFieldId)!.fieldEnumId = undefined
          }
        }
      }
      if (this.isCarOrVansElectricOnSg(fieldValuesArray, Constants.GROUP_EGNE_OG_LEASEDE_KØRETØJER, this.subgroupingId)) {
        // do not send both values to BE
        this.clearFieldIfHasValue(fieldValuesArray, Constants.INPUT_BIOBRÆNDSTOFANDEL);
      }
      if (this.hasBioFieldActiveOnSg(fieldValuesArray, Constants.GROUP_EGNE_OG_LEASEDE_KØRETØJER, this.subgroupingId)) {
        // do not send both values to BE
        this.clearFieldIfHasValue(fieldValuesArray, Constants.FIELD_GUARANTEES_OF_ORIGIN);
      }
      const calculationLine: Calculate = {
        id: this.currentCalculationLineId ? this.currentCalculationLineId : undefined,
        calculationId: this.calculation.id,
        subgroupingId: this.subgroupingId ?? 0,
        coeficientGroup: this.currentEF,
        fieldValues: fieldValuesArray,
        thirdpartyVatNo: this.thirdPartyInfo.behalfOfVatNr,
        updatedBy: this.thirdPartyInfo.loggedInVatNr,
        createdBy: this.thirdPartyInfo.loggedInVatNr,
        usedCountryCode: this.country,
        emissionCoefficentId: this.basisModule ? this.emcId : undefined,
        emissionCoefficentMasterId: this.basisModule ? this.emfId : undefined,
        emissionCoefficentMasterKey: this.basisModule ? this.emfKey : undefined,
      }

      const resetAfterSave = () => {
        this.currentCalculationLineId = undefined
        this.renderedFields.forEach((field) => (field.value = ''))
        this.scopeFields.forEach((field) => (field.value = ''))
        this.esgManualFields.forEach((field) => (field.value = ''))
        this.remarksField.value = ''
        if (this.basisModule) {
          this.emfKey = ''
          this.emcId = -1
          this.emfId = -1
        }
        this.resizeRemarksField()
        this.setTotalEmission('')
      }

      if (this.basisModule) {
        calculationLine.fieldValues.forEach(f => {
          if (f.value) {
            f.value = f.value.toString()
            if (f.fieldEnumId) {
              f.contentfulId = this.getContentfulId(f.fieldEnumId)
            }
          }
        })
        if (calculationLine.calculationId == undefined) {
          calculationLine.calculationId = -1 // Use some fake ID, since it is required field
        }
        if (calculationLine.id == undefined || calculationLine.id < 0) {
          calculationLine.id = store.getters.getBasisModuleLineId
        }
        this.$emit('saveCalculationLine', calculationLine)
        this.updateCalculationListBasisModule(calculationLine)
        resetAfterSave()
        await this.loadFields();
      } else {
        const file1 = this.files && this.files.length > 0 ? this.files[0] : undefined
        const file2 = this.files && this.files.length > 1 ? this.files[1] : undefined
        const file3 = this.files && this.files.length > 2 ? this.files[2] : undefined

        await calculateClient.calculateAndSave(JSON.stringify(calculationLine), file1, file2, file3)
          .then(async (response) => {
            result = response.data
            const targetCalculateId = this.currentCalculationLineId
            this.lastSavedCalculationId = response.data.id ?? 0 // calculateId

            const toastTitle = this.renderedFields[0].value
            const toastMessageType = this.currentCalculationLineId ? this.constants.TOAST_TYPE_CALCULATOR_UPDATED : this.constants.TOAST_TYPE_CALCULATOR_SAVED

            resetAfterSave()
            await this.loadFields(targetCalculateId)
            this.hasBeenRecalculated = false
            this.showToastAndDelayHide(toastTitle, toastMessageType)
          })
          .catch((error) => {
            console.error('[fejl] Indlæsningsfejl:', {
              message: error.message,
              response: error.response ? error.response.data : null,  // Backend response (if any)
              status: error.response ? error.response.status : null,  // HTTP status code
              stack: error.stack,  // Stack trace for debugging
              config: error.config  // Request config (useful for debugging request issues)
            });
            if (error.response?.data?.errorCode === 'FILE_HAS_VIRUS') {
              const errorText = this.store.getters.getContentfulContent.findSimpleText('virus.scanner.errortext')
                .replace("#ERROR_MESSAGE#", error.response.data.message)
              this.formValidation.formErrors.set('file-error', errorText)
              return
            } else if (error.response?.data?.errorCode === 'VIRUS_SCANNER_NOT_RUN') {
              const errorText2 = this.store.getters.getContentfulContent.findSimpleText('virus.scanner.not-run.errortext')
                .replace("#ERROR_MESSAGE#", error.response.data.message)
              this.formValidation.formErrors.set('file-error', errorText2)
              return
            } else if (error.response?.data?.errorCode === 'EMISSION_COEFFICIENT_VERSION_OUTDATED') {
              const errorText = this.store.getters.getContentfulContent.findSimpleText('calculate.outdated-emission-coefficient-version')
              this.formValidation.formErrors.set('global', errorText)
              return
            } else {
              this.formValidation.formErrors.set('file-error', this.fileErrorGeneral)
              return
            }
          })
      }
    } else {
      if (this.skipCalculate(fieldValuesArray, this.subgroupingId || -1)) {
        //console.log("Skipcalculate")
        return
      }

      const resultCallback = (result: Calculate) => {
        this.updateScopeFields(result)
        this.setTotalEmission(this.calculateTotalEmission(result))
        if (this.calculation.companyInformation?.country !== 'DK' && !this.basisModule) {
          this.showEmissionWarning = result.usedCountryCode === this.calculation.companyInformation?.country
          // kk2-1260 for foreign own input sections warning should newer be shown
          if (this.scopeFields[0].valueType.includes('OWN')
            || this.scopeFields[1].valueType.includes('OWN')
            || this.scopeFields[2].valueType.includes('OWN')
            || this.scopeFields[3].valueType.includes('OWN')
          ) {
            this.showEmissionWarning = false
          }
        }
        if (this.basisModule) {
          this.currentCalculationLineId = result.id
        }
        this.emcId = result.emissionCoefficentId ?? -1
        if (result.emissionCoefficentMasterId) {
          this.emfId = result.emissionCoefficentMasterId
        }
        if (result.emissionCoefficentMasterKey) {
          this.emfKey = result.emissionCoefficentMasterKey
        }
        this.hasBeenRecalculated = true
      }

      await calculateClient
        .calculate({
          id: this.basisModule ? this.currentCalculationLineId : undefined,
          calculationId: this.basisModule ? -1 : this.calculation.id,
          subgroupingId: this.subgroupingId ?? 0,
          coeficientGroup: this.currentEF,
          fieldValues: fieldValuesArray,
          thirdpartyVatNo: this.thirdPartyInfo.behalfOfVatNr,
          updatedBy: this.thirdPartyInfo.loggedInVatNr,
          createdBy: this.thirdPartyInfo.loggedInVatNr,
          isBasisModule: this.basisModule
        })
        .then((response) => {
          result = response.data
          resultCallback(result)
        })
        .catch((error) => {
          console.warn('[Advarsel] Indlæsningsfejl: ' + error)
        })
    }
    return result.id
  }

  private clearFieldIfHasValue(fieldValues: FieldValue[], fieldId: number): void {
    const field = fieldValues.find(f => f?.id === fieldId);
    if (field?.value) {
      //console.log(`clear field ${fieldId}`);
      field.value = '';
    }
  }

  getContentfulId(fieldEnumId: number): string {
    for (const field of this.fields) {
      const foundValue = field.possibleValues?.find(possibleValue => possibleValue.id === fieldEnumId)
      if (foundValue && foundValue.contentfulId) {
        return foundValue.contentfulId
      }
    }
    return ''
  }

  private hookEsgDependents(fields: CombinedField[]) {
    if (this.isSubgroupWithPossibleOwnEsgFactors()) {
      fields.forEach(field => {
        if (field.valueType === 'UNIT' || field.valueType === 'DROPDOWN_DEPENDENT' || field.name === 'Enhed_affald') {
          const unitValue = this.findFieldEnumNameByEnumID(field)
          //console.log("BaseCalculator, UPDATE CALCULATION HOOK, field.id=" + field.id + " field.value=" + field.value + " field.valueType=" + field.valueType, unitValue, field)
          if (unitValue === 'KWh') {
            this.esgManualFields[4].value = '0.001'
            this.esgManualFields[4].isEnabled = false
          } else if (unitValue === 'MWh') {
            this.esgManualFields[4].value = '1'
            this.esgManualFields[4].isEnabled = false
          } else if (unitValue === 'GWh') {
            this.esgManualFields[4].value = '1000'
            this.esgManualFields[4].isEnabled = false
          } else {
            this.esgManualFields[4].isEnabled = true
          }
        }
      })
    }
  }

  private findUnitValueName(): string | undefined {
    let field = this.fields.find(f => f.valueType === 'UNIT')
    if (!field) {
      // Special handling of input sections that uses an old-school DROPDOWN_DEPENDENT field as a unit field (ids are used)
      field = this.fields.find(f => f.valueType == 'DROPDOWN_DEPENDENT' && (
        f.id === this.constants.INPUT_DROPDOWN_DEPENDENT_UNIT_1
        || f.id === this.constants.INPUT_DROPDOWN_DEPENDENT_UNIT_2
        || f.id === this.constants.INPUT_DROPDOWN_DEPENDENT_UNIT_3)
      )
    }
    return (field ? this.findFieldEnumNameByEnumID(field) : undefined)
  }

  private findFieldEnumNameByEnumID(field: CombinedField) {
    return field.possibleValues?.find(f => f.id === Number(field.value.substring(6)))?.name
  }

  skipCalculate(fieldValuesArray: Array<FieldValue>, subgroupingId: number) {
    // is there a better way?? do not call BE for the below combinations
    if (fieldValuesArray && fieldValuesArray.length > 3) {
      //console.log("check for skip felt2: '", fieldValuesArray[1].value, "', felt3: '", fieldValuesArray[2].value, "', sg: ", subgroupingId, "slot:", this.getFieldByCalculatorPosition(Constants.CALCULATOR_SLOT_FELT3).value)
      if (fieldValuesArray[3].id === this.constants.INPUT_ENHED_KM_L_KWH && fieldValuesArray[3].value === this.constants.INPUT_ENHED_L) {
        if (fieldValuesArray[1].id === this.constants.INPUT_DROPDOWN_TRANSPORT_DRIVMIDDEL_E &&
          (fieldValuesArray[1].value.includes('(El)'))) {
          return true
        }
      }
      const subgroups: Array<number> = [
        this.constants.GROUP_INDKØB_AF_PRODUKTER_I_MONETÆRE_ENHEDER,
        this.constants.GROUP_INDKØB_AF_PRODUKTER_I_MONETÆRE_ENHEDER_II,
        this.constants.GROUP_INDKØB_AF_MATERIALER_I_FYSISKE_ENHEDER,
        this.constants.GROUP_INDKØB_AF_MATERIALER_I_FYSISKE_ENHEDER_II,
        this.constants.GROUP_INDKØB_AF_MATERIALER_I_MONETÆRE_ENHEDER,
        this.constants.GROUP_INDKØB_AF_MATERIALER_I_MONETÆRE_ENHEDER_II]
      if (subgroups.includes(subgroupingId)) {
        if (fieldValuesArray[1].id === this.constants.DROPDOWN_PRODUKT_GRUPPE) {
          if (fieldValuesArray[2].id === this.constants.DROPDOWN_SPECIFIKKE_PRODUKTER) {
            if (fieldValuesArray[1].value === 'Plast, færdigvarer' && fieldValuesArray[2].value !== 'Uspecificeret') {
              // clear scope3 and sum
              this.scopeFields[2].value = ''
              this.totalEmission = ''
              return true
            }
            if (fieldValuesArray[1].value === 'Elektronik' && fieldValuesArray[2].value !== 'Generelt') {
              // clear scope3 and sum
              this.scopeFields[2].value = ''
              this.totalEmission = ''
              return true
            }
          }
        }
        if (fieldValuesArray[1].id === this.constants.DROPDOWN_MATERIALE_GRUPPE || fieldValuesArray[1].id === this.constants.DROPDOWN_MATERIALE_GRUPPE_II) {
          if (fieldValuesArray[2].id === this.constants.DROPDOWN_SPECIFIKKE_MATERIALER) {
            if (fieldValuesArray[1].value === 'Tekstil og garn' && fieldValuesArray[2].value !== 'Uspecificeret') {
              // clear scope3 and sum
              this.scopeFields[2].value = ''
              this.totalEmission = ''
              return true
            }
          }
        }
      }
    }
    return false
  }

  updateScopeFields(calculate: Calculate) {
    this.scopeFields.forEach((field) => {
      const newScope = calculate.fieldValues?.find((cal) => field.id === cal.id)
      if (newScope)
        Object.assign(field, newScope)
    })
  }

  applyValidationError(element: HTMLInputElement, errortext: string) {
    this.formValidation.formErrors.set(element.id, errortext)
  }

  applyValidationOk(element: HTMLInputElement) {
    this.formValidation.formErrors.delete(element.id)
  }

  validateEsgManualFields(): boolean {
    let validateOK = true
    if (this.isSubgroupWithPossibleOwnEsgFactors() && this.useInEsg) {
      this.formValidation.formErrors.delete('esg-percentage-sum-not-100')
      this.formValidation.formErrors.delete('esg-conversion-factor-0')
      this.formValidation.formErrors.delete('esg-conversion-factor-1-for-non-mwh')
      const percentageSum = Number(this.esgManualFields[1].value)
        + Number(this.esgManualFields[2].value)
        + Number(this.esgManualFields[3].value)
      if (percentageSum < 99.9999 || percentageSum > 100.0001) {
        this.formValidation.formErrors.set('esg-percentage-sum-not-100', this.store.getters.getContentfulContent.findSimpleText('generic-calculation.error.esg.percentage-sum-not-100'))
        validateOK = false
      }
      if (Number(this.esgManualFields[4].value) === 0) {
        this.formValidation.formErrors.set('esg-conversion-factor-0', this.store.getters.getContentfulContent.findSimpleText('generic-calculation.error.esg.conversion-factor-0'))
        validateOK = false
      }
      if (Number(this.esgManualFields[4].value) === 1 && this.findUnitValueName() !== 'MWh') {
        this.formValidation.formErrors.set('esg-conversion-factor-1-for-non-mwh', this.store.getters.getContentfulContent.findSimpleText('generic-calculation.error.esg.conversion-factor-1-for-non-mwh'))
        validateOK = false
      }
    }
    return validateOK
  }

  async applyConditions(paramValue?: any) {
    this.working = true
    //console.log("ApplyConditions start", paramValue, "all, renderedFields:", this.renderedFields)

    for (const combinedItem of this.renderedFields) {
      const selectedDDvalue = combinedItem.value
      if (combinedItem.possibleValues) {
        for (const item of combinedItem.possibleValues) {
          // TODO: not enough to take name (e.g. tow drikkevarer in sg14 Fødevarer), we must use id
          // but then genericInput must be changed
          if (item.name === selectedDDvalue && selectedDDvalue !== '') {
            //console.log("hit:", item, "comb:", combinedItem)
            combinedItem.fieldEnumId = item.id
          }
        }
      }
    }
    if (this.renderedFields[1] && this.renderedFields[1].id === this.constants.INPUT_BRAENDSELSTYPE) {
      if (this.renderedFields[1].value !== this.constants.FE_ID_INPUT_ANDEL_BIOGAS) {
        this.renderedFields[2].value = ''
      }
    }

    //Special conditional for E05
    if (this.renderedFields[4] && this.renderedFields[4].id === this.constants.INPUT_BIOBRÆNDSTOFANDEL || (this.constants.FIELD_ENUM_ID_PREFIX + this.renderedFields[4].id) === this.constants.FE_ID_INPUT_ANDEL_BIOGAS) {
      if (this.isDisableBioPart(this.renderedFields[1], this.renderedFields[3])) {
        this.renderedFields[4].value = ''
      }
    }
    await this.updateCalculation(false)
    //Following if statement is necessary to handle form-errors outside submit.
    if (this.formValidation.formErrors) {
      this.focusFirstError(this.formValidation)
    }
    this.working = false
    //console.log("ApplyConditions end", paramValue, "all", this.renderedFields)
  }

  async submitForm(e: Event) {
    //console.log("submitForm start", e)
    const submitter = e as SubmitEvent
    const submitterHtml: string = submitter.submitter.outerHTML
    if (submitterHtml.includes('updateCalculationButton')) {
      await this.updateCalculation(false)
      return
    }

    if (!this.validateEsgManualFields()) {
      return
    }

    this.formValidation.formErrors.clear()
    let currentCalculateLineId = 0
    this.working = true
    await this.updateCalculation(true).then(async (response) => {
      currentCalculateLineId = response ?? 0

      this.files = []
      this.fileList = []
      this.showEsgFields = true
      await this.loadCalculations(currentCalculateLineId)
      this.working = false
      let filesReload: FileMessage[] | undefined = []
      if (currentCalculateLineId && currentCalculateLineId > 0) {
        await fileClient
          .getFilesByCalculationLineId(currentCalculateLineId, this.thirdpartyVatNo())
          .then(async (response) => {
            filesReload = response.data.files
            if (this.genericTableComponentRefs[currentCalculateLineId]) await this.genericTableComponentRefs[currentCalculateLineId].getFiles(filesReload)
          })
          .catch((error) => {
            console.warn(error)
          })
      }
    })
  }

  biofuelInput(e: Event) {
    const element = e.target as HTMLInputElement
    this.renderedFields[4].value = element.value ? element.value : ''
  }

  biofuelInputAndSetCalculationChanged(e: Event) {
    this.biofuelInput(e);
    this.setCalculationChanged()
  }

  unitValueChanged(oldValue: string, newValue: string) {
    //console.log("unitValueChanged ", oldValue, newValue)
    //if esg manual then clear fields
    if (this.isSubgroupWithPossibleOwnEsgFactors()) {
      if (newValue !== oldValue) {
        this.esgManualFields.filter(f => f.valueType === 'DECIMAL').forEach((field) => (field.value = ''))
      }
      this.hookEsgDependents(this.fields)
    }
  }

  private isSubgroupWithPossibleOwnEsgFactors() {
    return Constants.GROUPS_WITH_POSSIBLE_OWN_ESG_FACTORS.includes(this.subgroupingId ?? -1)
  }

  async setSelectedDependent(fieldId: number, argument: any) {
    //console.log("setSelectedDependent start, fieldId="+fieldId, argument, "renderegfields:", this.renderedFields)
    if (this.renderedFields.some((field) => field.id === fieldId)) {
      const field = this.renderedFields?.find((field) => field.id === fieldId)
      if (field && (field.selectedDependents !== argument)) {
        field.selectedDependents = argument
        await this.applyConditions()
      }
    }
  }

  setCalculationChanged() {
    //console.log("setCalculationChanged", document.getElementsByClassName("form-input changed").length > 0, document.getElementsByClassName("form-input changed").length, document.getElementsByClassName("form-input changed"))
    this.calculationChanged = document.getElementsByClassName('form-input changed').length > 0
  }

  async updatedEmissionFactorToggle(arg: string) {
    this.currentEF = arg
    if (this.calculation.id) {
      await calculateClient
        .declarationCalculations(this.calculation.id, arg === 'Miljødeklaration', this.thirdpartyVatNo())
        .then(async (result) => {
          const newCalculation = {
            ...this.calculation,
            electricityEnvironmentDeclaration: arg === 'Miljødeklaration'
          }
          newCalculation.electricityEnvironmentDeclaration = arg === 'Miljødeklaration'

          await this.store.dispatch('setCalculation', newCalculation).then(async () => {
            await this.loadCalculations().then(async () => {
              await this.updateCalculation(false)
            })
          })
        })
        .catch((error) => {
          console.warn('[Advarsel] Indlæsningsfejl: ' + error)
        })
    }
  }

  async updatedRfiToggle(arg: string) {
    if (this.calculation.id) {
      await calculateClient
        .rfiCalculations(this.calculation.id, arg === 'includeRfi', this.thirdpartyVatNo())
        .then(() => {
          const newCalculation = { ...this.calculation }
          newCalculation.rfi = arg === 'includeRfi'

          this.store.dispatch('setCalculation', newCalculation).then(async () => {
            await this.loadCalculations().then(async () => {
              await this.updateCalculation(false)
            })
          })
        })
        .catch((error) => {
          console.warn('[Advarsel] Indlæsningsfejl: ' + error)
        })
    }
  }

  get constants() {
    return Constants
  }


  asContentfulTooltip(id: number) {
    switch (id) {
      //Energi og processer
      case this.constants.GROUP_FORBRUG_AF_ELEKTRICITET_MED_EGNE_EMISSIONSFAKTORER:
        return this.getContentfulString('calculator.electricity_own_emissionfactors.scope3.category.tooltip')
      case this.constants.GROUP_FORBRUG_AF_ELEKTRICITET:
      case this.constants.GROUP_FJERNVARME:
      case this.constants.GROUP_BRÆNDSLER:
        return this.getContentfulString('calculator.electricity.scope3.category.tooltip')
      case this.constants.GROUP_PROCESUDLEDNING:
        return this.getContentfulString('calculator.process_emission.scope3.category.tooltip')
      case this.constants.GROUP_PROCESUDLEDNING_MED_EGNE_EMISSIONSFAKTORER:
        return this.getContentfulString('calculator.process_emission_own_emissionfactors.scope3.category.tooltip')
      case this.constants.GROUP_VARME_OG_PROCESENERGI_MED_EGNE_EMISSIONSFAKTORER:
        return this.getContentfulString('calculator.heat_process_own_emissionfactors.scope3.category.tooltip')
      case this.constants.GROUP_ANDET_UDLEDNING_VED_ENERGI_OG_PROCESSER:
        return this.getContentfulString('calculator.other_emission_heat_process.scope3.category.tooltip')
      //Indkøb
      case this.constants.GROUP_INDKØB_AF_MATERIALER_I_FYSISKE_ENHEDER:
      case this.constants.GROUP_INDKØB_AF_MATERIALER_I_MONETÆRE_ENHEDER:
      case this.constants.GROUP_INDKØB_AF_MATERIALER_MED_EGNE_EMISSIONSFAKTORER_I_FYSISKE_ENHEDER:
      case this.constants.GROUP_INDKØB_AF_MATERIALER_MED_EGNE_EMISSIONSFAKTORER_I_MONETÆRE_ENHEDER:
        return this.getContentfulString('calculator.purchase.scope3.category.tooltip')
      case this.constants.GROUP_INDKØB_AF_PRODUKTER_I_FYSISKE_ENHEDER:
        return this.getContentfulString('calculator.purchase.scope3.category.tooltip')
      case this.constants.GROUP_INDKØB_AF_PRODUKTER_I_MONETÆRE_ENHEDER:
        return this.getContentfulString('calculator.purchase_products_money_units.scope3.category.tooltip')
      case this.constants.GROUP_INDKØB_AF_PRODUKTER_MED_EGNE_EMISSIONSFAKTORER_I_FYSISKE_ENHEDER:
      case this.constants.GROUP_INDKØB_AF_PRODUKTER_MED_EGNE_EMISSIONSFAKTORER_I_MONETÆRE_ENHEDER:
      case this.constants.GROUP_ANDET_UDLEDNING_VED_PRIMÆRE_INDKØB_I_FYSISKE_ENHEDER:
      case this.constants.GROUP_ANDET_UDLEDNING_VED_PRIMÆRE_INDKØB_I_MONETÆRE_ENHEDER:
        return this.getContentfulString('calculator.purchase_iii.scope3.category.tooltip')
      case this.constants.GROUP_INDKØB_AF_MATERIALER_I_FYSISKE_ENHEDER_II:
      case this.constants.GROUP_INDKØB_AF_MATERIALER_I_MONETÆRE_ENHEDER_II:
      case this.constants.GROUP_INDKØB_AF_MATERIALER_MED_EGNE_EMISSIONSFAKTORER_I_FYSISKE_ENHEDER_II:
      case this.constants.GROUP_INDKØB_AF_MATERIALER_MED_EGNE_EMISSIONSFAKTORER_I_MONETÆRE_ENHEDER_II:
        return this.getContentfulString('calculator.purchase_ii.scope3.category.tooltip')
      case this.constants.GROUP_INDKØB_AF_PRODUKTER_I_FYSISKE_ENHEDER_II:
        return this.getContentfulString('calculator.purchase.scope3.category.tooltip')
      case this.constants.GROUP_INDKØB_AF_PRODUKTER_I_MONETÆRE_ENHEDER_II:
        return this.getContentfulString('calculator.purchase_products_money_units_secondary.scope3.category.tooltip')
      case this.constants.GROUP_INDKØB_AF_PRODUKTER_MED_EGNE_EMISSIONSFAKTORER_I_FYSISKE_ENHEDER_II:
      case this.constants.GROUP_INDKØB_AF_PRODUKTER_MED_EGNE_EMISSIONSFAKTORER_I_MONETÆRE_ENHEDER_II:
      case this.constants.GROUP_ANDET_UDLEDNING_VED_SEKUNDÆRE_INDKØB_I_FYSISKE_ENHEDER:
      case this.constants.GROUP_ANDET_UDLEDNING_VED_SEKUNDÆRE_INDKØB_I_MONETÆRE_ENHEDER:
        return this.getContentfulString('calculator.purchase_iiii.scope3.category.tooltip')
      //Transport
      case this.constants.GROUP_EGNE_OG_LEASEDE_KØRETØJER:
        return this.getContentfulString('calculator.transport_own.scope3.category.tooltip')
      case this.constants.GROUP_FLY_OG_SKIBE:
        return this.getContentfulString('calculator.transport_items.scope3.category.tooltip')
      case this.constants.GROUP_EGNE_OG_LEASEDE_TRANSPORTMIDLER_MED_EGNE_EMISSIONSFAKTORER:
        return this.getContentfulString('calculator.transport_own_own_emissionfactors.scope3.category.tooltip')
      case this.constants.GROUP_PENDLING:
        return this.getContentfulString('calculator.group_pendling.scope3.category.tooltip')
      case this.constants.GROUP_REJSER_I_FORBINDELSE_MED_ARBEJDE_FYSISKE_ENHEDER:
      case this.constants.GROUP_REJSER_I_FORBINDELSE_MED_ARBEJDE_MONETÆRE_ENHEDER_BELØB_I_DKK:
        return this.getContentfulString('calculator.group_travel_work_or_money.scope3.category.tooltip')
      case this.constants.GROUP_MEDARBEJDERTRANSPORT_MED_EGNE_EMISSIONSFAKTORER:
        return this.getContentfulString('calculator.group_employee_transport_own_ef.scope3.category.tooltip')
      case this.constants.GROUP_VARETRANSPORT_I_FYSISKE_ENHEDER:
      case this.constants.GROUP_VARETRANSPORT_I_MONETÆRE_ENHEDER_BELØB_I_DKK:
      case this.constants.GROUP_VARETRANSPORT_TIL_VIRKSOMHED_MED_EGNE_EMISSIONSFAKTORER:
        return this.getContentfulString('calculator.group_product_transport.scope3.category.tooltip')
      case this.constants.GROUP_VARETRANSPORT_I_MONETÆRE_ENHEDER_BELØB_I_DKK_II:
      case this.constants.GROUP_VARETRANSPORT_FRA_VIRKSOMHED_TIL_KUNDE_MED_EGNE_EMISSIONSFAKTORER:
      case this.constants.GROUP_VARETRANSPORT_I_FYSISKE_ENHEDER_II:
        return this.getContentfulString('calculator.group_product_transport_ii.scope3.category.tooltip')
      case this.constants.GROUP_TRANSPORT_ANDET:
        return this.getContentfulString('calculator.group_other_general.scope3.category.tooltip')
      //Solgte produkter
      case this.constants.GROUP_BRUG_AF_SOLGTE_OG_UDLEJEDE_PRODUKTER:
      case this.constants.GROUP_BRUG_AF_SOLGTE_OG_UDLEJEDE_PRODUKTER_MED_EGNE_EMISSIONSFAKTORER:
        return this.getContentfulString('calculator.group_use_of_sold_or_leased_product.scope3.category.tooltip')
      case this.constants.GROUP_ANDET_VED_SOLGTE_PRODUKTER:
        return this.getContentfulString('calculator.group_other_sold_products.scope3.category.tooltip')
      case this.constants.GROUP_FORARBEJDNING_OG_PROCESSERING_AF_SOLGTE_PRODUKTER:
        return this.getContentfulString('calculator.group_sold.scope3.category.tooltip')
      case this.constants.GROUP_END_OF_LIFE_BEHANDLING:
      case this.constants.GROUP_END_OF_LIFE_BEHANDLING_MED_EGNE_EMISSIONSFAKTORER:
        return this.getContentfulString('calculator.group_end_of_life.scope3.category.tooltip')
      //Affald
      case this.constants.GROUP_AFFALD:
      case this.constants.GROUP_ANDET_UDLEDNING_VED_AFFALD_OG_GENBRUG:
        return this.getContentfulString('calculator.group_waste_other_reuse.scope3.category.tooltip')
      case this.constants.GROUP_AFFALD_MED_EGNE_EMISSIONSFAKTORER:
        return this.getContentfulString('calculator.group_waste_own.scope3.category.tooltip')
      default:
        return 'Tooltip mangler for id: ' + id
    }
  }

  private resizeRemarksField() {
    const remarksFieldElem = document.getElementById(`calculator-${this.subgroupingId}-input-area`)
    if (remarksFieldElem) {
      remarksFieldElem.removeAttribute('style')
    }
  }

  getFieldByCalculatorPosition(position: number) {
    // Alert if there is more than 1 field in the same position, that is enabled
    if (this.fields.filter(x => x.calculatorSlotPosition === position && x.isEnabled).length > 1) {
      console.error('CRITICAL: Der er mere end ét felt der er aktivt på position: ' + position)
    }
    const field = this.fields.find(x => x.calculatorSlotPosition === position && x.isEnabled)
    if (this.fields && field) {
      return field
    }
    return {} as CombinedField
  }

  getFieldById(fieldId: number) {
    // Alert if there is more than 1 field in the same position, that is enabled
    if (this.fields.filter(x => x.id === fieldId && x.isEnabled).length > 1) {
      console.error('CRITICAL: Der er mere end ét felt der er aktivt på position: ' + fieldId)
    }
    const field = this.fields.find(x => x.id === fieldId && x.isEnabled)
    if (this.fields && field) {
      return field
    }
    return {} as CombinedField
  }

  getFieldByName(fieldName: string) {
    // Alert if there is more than 1 field in the same position, that is enabled
    if (this.fields.filter(x => x.name === fieldName && x.isEnabled).length > 1) {
      console.error('CRITICAL: Der er mere end ét felt for field name: ' + fieldName)
    }
    const field = this.fields.find(x => x.name === fieldName && x.isEnabled)
    if (this.fields && field) {
      return field
    }
    return {} as CombinedField
  }

  get totalEmissionsTons() {
    return this.store.getters.getContentfulContent.findSimpleText('calculation.total-emissions.field.label')
  }

  get countryEmissionWarning() {
    return this.store.getters.getContentfulContent.findSimpleText('sharedContent.countryEmissionWarning')
  }

  get deleteRowToastHeader() {
    return this.store.getters.getContentfulContent.findSimpleText('genericcalculation.deleteRowToastHeader')
  }

  get deleteRowToastMessage() {
    return this.store.getters.getContentfulContent.findSimpleText('genericcalculation.deleteRowToastMessage')
  }

  get saveRowToastHeader() {
    return this.store.getters.getContentfulContent.findSimpleText('genericcalculation.saveRowToastHeader')
  }

  get saveRowToastMessage() {
    return this.store.getters.getContentfulContent.findSimpleText('genericcalculation.saveRowToastMessage')
  }

  get updateRowToastHeader() {
    return this.store.getters.getContentfulContent.findSimpleText('genericcalculation.updateRowToastHeader')
  }

  get updateRowToastMessage() {
    return this.store.getters.getContentfulContent.findSimpleText('genericcalculation.updateRowToastMessage')
  }

  get saveErrorToastHeader() {
    return this.store.getters.getContentfulContent.findSimpleText('genericcalculation.saveErrorToastHeader')
  }

  get saveErrorToastMessage() {
    return this.store.getters.getContentfulContent.findSimpleText('genericcalculation.saveErrorToastMessage')
  }

  get fileErrorGeneral() {
    return this.store.getters.getContentfulContent.findSimpleText('genericcalculation.fileErrorGeneral')
  }

  get fileErrorMaxFiles() {
    return this.store.getters.getContentfulContent.findSimpleText('genericcalculation.fileErrorMaxFiles')
  }

}